import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { Subject } from 'rxjs';
import { distinctUntilChanged, filter, map, takeUntil } from 'rxjs/operators';

import { WorkOrder } from '@app/dashboard/work-order.model';
import { WorkOrderDetailsService } from '@app/work-order/work-order-details.service';
import { WorkOrderService } from '@app/dashboard/work-order.service';
import { DashboardState } from '@app/core/store/reducers/dashboard';
import { Store } from '@ngrx/store';
import { SetSearchText } from '@dwo/actions';
import { FeatureFlagService } from '@app/core/services/feature-flag.service';
import { FeatureFlagKey } from '@dwo/enums';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { OfflineDownloadService } from '@app/work-order/offline-download/offline-download.service';
import {
  OfflineDownloadDialogComponent,
  OfflineDownloadDialogData,
} from '@app/shared/offline-download-dialog/offline-download-dialog.component';

// Inline so it renders offline without fetching an icon font or SVG file.
const DATABASE_ICON_SVG =
  '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" ' +
  'stroke-linecap="round" stroke-linejoin="round"><ellipse cx="12" cy="5" rx="8" ry="3"/>' +
  '<path d="M4 5v14c0 1.66 3.58 3 8 3s8-1.34 8-3V5"/><path d="M4 12c0 1.66 3.58 3 8 3s8-1.34 8-3"/></svg>';

@Component({
  selector: 'app-work-order-card',
  templateUrl: './work-order-card.component.html',
  styleUrls: ['./work-order-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class WorkOrderCardComponent implements OnInit, OnDestroy {
  @Input() workOrder!: WorkOrder;
  @Input() isOnline = navigator.onLine;
  @Input() isOfflineFallback = false;
  @Input() showProjectTitle = false;
  @Input() projectTitle = '';
  @Input() hasNotification = false;
  @Input() hasNewNotification = false;
  @Input() refreshPerformed = false;

  @Output() downloaded = new EventEmitter<WorkOrder>();
  @Output() downloadRemoved = new EventEmitter<WorkOrder>();
  @Output() toggleFavourite = new EventEmitter();

  public isDownloading = false; // Deprecated: use workOrder.isDownloading for global sync state
  private readonly destroy$ = new Subject<void>();

  constructor(
    private workOrderDetailsService: WorkOrderDetailsService,
    private workOrderService: WorkOrderService,
    private store: Store<DashboardState>,
    private featureFlagService: FeatureFlagService,
    private router: Router,
    private dialog: MatDialog,
    private offlineDownloadService: OfflineDownloadService,
    iconRegistry: MatIconRegistry,
    sanitizer: DomSanitizer,
  ) {
    iconRegistry.addSvgIconLiteral('database', sanitizer.bypassSecurityTrustHtml(DATABASE_ICON_SVG));
  }

  ngOnInit(): void {
    const workOrderId = this.workOrder.id;
    let wasRunning = false;
    this.offlineDownloadService
      .state$(workOrderId)
      .pipe(
        map((state) => state.running),
        distinctUntilChanged(),
        takeUntil(this.destroy$),
      )
      .subscribe((running) => {
        // Only react to dialog-started downloads so the initial idle state doesn't clear a Sync all spinner.
        if (running || wasRunning) {
          this.workOrder.isDownloading = running;
        }
        wasRunning = running;
      });

    this.offlineDownloadService.completed$
      .pipe(
        filter((result) => result.workOrderId === workOrderId && result.success),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        this.workOrder.downloaded = true;
        this.downloaded.emit(this.workOrder);
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get showDownloadButton(): boolean {
    return this.featureFlagService.isEnabled(FeatureFlagKey.ShowDownloadButton, this.workOrder?.projectId);
  }

  get showWeightBadge(): boolean {
    return (
      this.featureFlagService.isEnabled(FeatureFlagKey.EnableWeightHandling, this.workOrder?.projectId) &&
      (this.workOrder?.weightLineTotalCount ?? 0) > 0
    );
  }

  /** Unsent weight lines on a finished work order need to stand out. */
  get weightBadgeAlert(): boolean {
    const counts = this.workOrder;
    return (counts?.weightLineTotalCount ?? 0) - (counts?.weightLineSentCount ?? 0) > 0 && counts?.progress >= 100;
  }

  get weightBadgeComplete(): boolean {
    const counts = this.workOrder;
    const total = counts?.weightLineTotalCount ?? 0;
    const sent = counts?.weightLineSentCount ?? 0;
    return total > 0 && sent >= total;
  }

  openWeightReport(event: MouseEvent): void {
    event.stopPropagation();
    this.router.navigate(['/work-order', this.workOrder.id, 'weight-report']);
  }

  openComments(event: MouseEvent): void {
    event.stopPropagation();
    this.router.navigate(['/work-order', this.workOrder.id, 'comments', 'WORK_ORDER']);
  }

  public async toggleDownload() {
    if (this.workOrder.isDownloading || this.isOfflineDownloadedLocked) {
      return;
    }

    const workOrderId = this.workOrder.id;
    const doRefresh =
      !this.refreshPerformed && this.workOrder.downloaded && (this.hasNotification || this.hasNewNotification);

    if (!this.workOrder.downloaded || doRefresh) {
      await this.openDownloadDialog();
      return;
    }

    this.workOrder.isDownloading = true;
    try {
      if (await this.workOrderDetailsService.isWorkOrderDownloaded(workOrderId)) {
        await this.workOrderDetailsService.removeDownloadWorkOrder(workOrderId);
      }
      this.workOrder.downloaded = false;
      this.downloadRemoved.emit(this.workOrder);
    } finally {
      this.workOrder.isDownloading = false;
    }
  }

  private async openDownloadDialog(): Promise<void> {
    const data: OfflineDownloadDialogData = {
      workOrderId: this.workOrder.id,
      workOrderNo: this.workOrder.workOrderNo,
      projectId: this.workOrder.projectId,
      downloaded: this.workOrder.downloaded,
      initialSelection: await this.offlineDownloadService.getStoredSelection(this.workOrder.id),
    };
    this.dialog.open(OfflineDownloadDialogComponent, {
      data,
      width: '880px',
      maxWidth: 'calc(100vw - 16px)',
      maxHeight: 'calc(100vh - 64px)',
      panelClass: 'offline-download-dialog',
    });
  }

  addToSearch(event: MouseEvent, functionalUnit: string) {
    event.stopPropagation();
    this.store.dispatch(new SetSearchText(functionalUnit));
  }

  async toggleFavouriteWorkOrder() {
    await this.workOrderService.toggleFavouriteWorkOrder(this.workOrder.id);
    this.toggleFavourite.emit();
  }

  get refreshRequired(): boolean {
    return (this.hasNotification || this.hasNewNotification) && !this.refreshPerformed;
  }

  public getIcon(): string {
    if (!this.workOrder.downloaded) {
      return 'save_alt';
    } else if (this.refreshRequired) {
      return 'refresh';
    } else {
      return 'database';
    }
  }

  get isOfflineDownloadedLocked(): boolean {
    const browserOnline = navigator.onLine;
    return !!this.workOrder.downloaded && (!this.isOnline || !browserOnline || this.isOfflineFallback);
  }
}
